/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useRef, useState } from "react";

import { BEM, EditContext, EditDnD, ComponentChildContext, IComponentChildContextValue, Viewport } from "@adapptio/lib-components";

import { ViewPreview } from "../../../../ui/components/ViewPreview";
import { useSimpleEvent } from "../../../../ui/hooks";
import { VIEW_EDITOR_VIEW_MODE, ViewEditorView } from "../ViewEditorView";
import { Icon, ICON_SIZE } from "../../../../ui/components/Icon";
import {
	getPreviewResolutionById,
	isBaseScreenResolutionId,
	usePreviewResolutionContext
} from "../../../../previewResolution";
import { IViewportConfig } from "@adapptio/lib-core";
import { IResizeObserverProps, ResizeObserver } from "../../../../ui/components/ResizeObserver";
import { isNil } from "@adapptio/lib-shared";

/**
 * Component props
 */
export interface IViewEditorPreviewProps {
	view: ViewEditorView;
	mode?: VIEW_EDITOR_VIEW_MODE;
}

/**
 * Main Application Component
 *
 * @param props Component props
 */
export const ViewEditorPreview: React.FunctionComponent<IViewEditorPreviewProps> = ({ view, mode }) => {
	const viewElRef = useRef<HTMLDivElement>();

	const editDnD = useMemo(() => new EditDnD(), []);

	const previewResolutionCtx = usePreviewResolutionContext();
	const { previewResolutionId, mediaResolutionId, manualWidth, safeAreaActive } = previewResolutionCtx;

	const model = view.getViewModel();
	const [ isViewAuthorized, setIsViewAuthorized ] = useState(
		() => model.getLastSpec()?.spec.isAuthorized ?? null
	);

	const [ blueprintModel, setBlueprintModel ] = useState(model.getModel());

	const handleViewportClick = useCallback(() => {
		model.getSelection().clear();
	}, [ model ]);

	const handlePreviewResize = useCallback<IResizeObserverProps["onResize"]>((entry) => {
		if (view.isViewActive()) {
			previewResolutionCtx.setPreviewWidth(entry.target.clientWidth);
		}

		window?.dispatchEvent(new CustomEvent(Viewport.UPDATE_EVENT_TYPE));
	}, []);

	const notifyContentChange = useCallback(() => {
		window?.dispatchEvent(new CustomEvent("adpt_editor_component_update"));
	}, []);

	useSimpleEvent(
		model.onRender,
		() => {
			setIsViewAuthorized(model.getLastSpec()?.spec.isAuthorized ?? null);
			notifyContentChange();
			setBlueprintModel(model.getModel());
		},
		[ model ],
		true
	);

	const viewportConfig = useMemo<IViewportConfig>(() => {
		const selectedResolution = getPreviewResolutionById(mediaResolutionId);
		const defaultWidth = isNil(selectedResolution?.screenDefault)
			? "100%"
			: `${selectedResolution.screenDefault}px`;

		const defaultConfig: IViewportConfig = {
			width: isNil(manualWidth) ? defaultWidth : `${manualWidth}px`,
			height: "100%",
			zoom: 1,
			showBoundaries: true,
			showSafeArea: safeAreaActive && mode === VIEW_EDITOR_VIEW_MODE.DESIGN
		};

		const minWidth = isBaseScreenResolutionId(previewResolutionId)
			? "800px"
			: `${selectedResolution.screenMin || 0}px`;
		const maxWidth = isNil(selectedResolution.screenMax) ? "unset" : `${selectedResolution.screenMax}px`;

		return {
			...defaultConfig,
			minWidth,
			maxWidth
		};
	}, [ mediaResolutionId, manualWidth, safeAreaActive, mode ]);

	// Child context is a memoized instance to avoid unneccessary re-renders
	// Child react components use only object references
	const childContext: IComponentChildContextValue = useMemo(
		() => ({
			dndState: {
				active: null
			}
		}),
		[]
	);

	const baseClass = BEM("edt-module-view-editor-preview", null, {
		"is-unauthorized": !isViewAuthorized
	});

	return (
		<EditContext.Provider
			value={{
				selection: model.getSelection(),
				componentDnD: editDnD,
				dCtx: model.getDesignContext(),
				media: mediaResolutionId
			}}
		>
			<ComponentChildContext.Provider value={childContext}>
				<div className={baseClass.toClassName()}>
					{isViewAuthorized === false ? (
						<div
							className={baseClass
								.element("unauthorized-notifcation")
								.add("edt-base")
								.toClassName()}
						>
							<Icon name="mdi/shield-lock" size={ICON_SIZE.SMALL} />
							{/* @todo Translate */}
							<p>
							Current user won't be able to access this view because authorization conditions
							are not met.
							</p>
						</div>
					) : null}
					<ResizeObserver
						onResize={() => window?.dispatchEvent(new CustomEvent(Viewport.RESIZE_EVENT_TYPE))}
					>
						<ViewPreview
							ref={viewElRef}
							renderContext={model.getRenderContext()}
							viewportConfig={viewportConfig}
							onResize={handlePreviewResize}
							mainComponentListModel={blueprintModel?.props.spec.value.props.content.value}
							onRootClick={handleViewportClick}
							className={baseClass.element("view-preview").toClassName()}
						/>
					</ResizeObserver>
				</div>
			</ComponentChildContext.Provider>
		</EditContext.Provider>
	);
};
